import { portalColors } from '../Publications/publicationModel';
// src/components/PropertyList/PDFPreviewEditor.jsx
//
// Editor visual del informe ACM. Cada página renderiza el fondo del PDF +
// overlays que matchean al render real (datos del wizard, agente, etc.) +
// inputs in-place editables con la MISMA tipografía/tamaño que aparece
// en el PDF final.
//
// Sistema de coordenadas:
//   - PDF jsPDF: landscape 320×297mm (pdfUtils.js:389). Origen top-left.
//   - Imágenes fondo: 2000×1856 px → ratio 1.077 = matchea 320/297. Sin distorsión.
//   - Slot coords en mm sobre el sistema A4-custom landscape.
//   - Preview escala mm → px linealmente.
//
// Tipografía en preview:
//   - jsPDF usa pt (points). 1pt = 0.3527mm.
//   - El PDF custom usa Helvetica como default.
//   - Para que un input del preview se vea IGUAL que el texto del PDF:
//     fontPx = ptPDF * (0.3527mm/pt) * (px/mm del preview).

import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { fetchBranding } from "../../api/brandingApi.js";
import { normalizeWizard } from "./utils/pdfUtils.js";
import {
  WIZARD_CHARACTERISTICS,
  formatCharacteristicDisplay,
} from "../../utils/characteristicFields.js";
import { calculateMapCenter, calculateZoomLevel } from "./utils/mapUtils.js";
import HorneroChat from "./HorneroChat.jsx";
import ReportPriceSuggestions from "./ReportPriceSuggestions.jsx";
import { areReportPricesComplete } from "./utils/priceReportLayout.js";
import { resolveReportContact } from "./utils/contactReportLayout.js";
import ReportContactPage, { ReportContactTools } from "./ReportContactPage.jsx";

import COVER_BACKGROUND from "../../assets/fondo.png";
import DOCUMENTACION from "../../assets/DOCUMENTACION.png";
import PROP from "../../assets/prop.png";

// ── Constantes del sistema de coords ────────────────────────────────────────
const PAGE_W_MM = 320;
const PAGE_H_MM = 297;
const PREVIEW_W_PX = 640;
const PREVIEW_H_PX = (PREVIEW_W_PX * PAGE_H_MM) / PAGE_W_MM;
const MM_TO_PX = PREVIEW_W_PX / PAGE_W_MM;
const PT_TO_MM = 0.3527;
const PT_TO_PX = PT_TO_MM * MM_TO_PX; // conversion final pt → px del preview

// Slugs EN ESPAÑOL — debe matchear pdfUtils.js:398-418 exactamente o el
// preview muestra "N/A" mientras el PDF muestra bien el tipo. Antes mi
// mapeo estaba en inglés (apartment/house/land) y el wizard usa slugs
// españoles (departamentos/casas/terrenos/etc) → caía siempre a N/A.
const typePropertyMap = {
  "quintas-vacacionales": "Quinta Vacacional",
  campos: "Campo",
  "bóvedas-nichos-y-parcelas": "Bóveda, Nicho y Parcela",
  terrenos: "Terreno",
  cocheras: "Cochera",
  "desarrollos-horizontales": "Desarrollo Horizontale",
  "desarrollos-verticales": "Desarrollo Vertical",
  hoteles: "Hotele",
  "oficinas-comerciales": "Oficina Comercial",
  depósitos: "Depósito",
  "locales-comerciales": "Local Comercial",
  edificios: "Edificio",
  "bodegas-galpones": "Bodega/Galpone",
  "fondos-de-comercio": "Fondo de Comercio",
  casas: "Casa",
  departamentos: "Departamento",
  oficinas: "Oficina",
  consultorios: "Consultorio",
  ph: "PH",
};

// Naming decente para tipos no mapeados — replica pdfUtils.js:211.
const humanizeType = (slug = "") =>
  slug
    ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "N/A";

const formatAmenities = (data = {}) => {
  const amenities = [];
  if (data.parking) amenities.push("Cochera");
  if (data.has_pool) amenities.push("Pileta");
  if (data.has_grill) amenities.push("Parrilla");
  if (data.has_garden) amenities.push("Jardín");
  if (data.has_terrace) amenities.push("Terraza");
  if (data.has_balcony) amenities.push("Balcón");
  return amenities.join(", ") || "Ninguna especificada";
};

/**
 * Catálogo de páginas dinámico según el branding del cliente. El PDF usa
 * los assets default como fallback, pero si el cliente subió portada/fondos
 * custom desde el dashboard (cover_image_url, additional_page1..4_url) los
 * usa en su lugar. El preview ahora replica ese comportamiento.
 *
 * Cada página tiene:
 *  - bg          → imagen de fondo (custom si hay, sino default)
 *  - slots       → inputs editables in-place (con coords mm + fontSize pt)
 *  - type        → identifica overlays especiales (wizard, contact)
 *  - showHeader  → true si se debe pintar el header (logo + brand bar) encima.
 *                  En el PDF lo hace addHeaderFooter() en todas las páginas
 *                  excepto Cover y Contacto.
 *  - isExtra     → true si es una página adicional (custom). Se puede sacar.
 */
const buildPages = (brand = {}, properties = [], wizardData = null) => {
  // Comparativa: 1 página por cada 6 propiedades activas. Si no hay props,
  // no agregamos páginas de comparativa.
  const compPagesCount = Math.ceil((properties?.length || 0) / 6);

  const pages = [
    {
      id: "cover",
      label: "Portada",
      bg: brand?.cover_image_url || COVER_BACKGROUND,
      slots: [],
      showHeader: false,
    },
    {
      id: "wizard",
      label: "Datos buscados",
      bg: PROP,
      slots: [],
      type: "wizard",
      showHeader: true,
    },
    // Mapa con las propiedades. Solo aparece si hay al menos 1 prop con
    // coordenadas válidas. El render hace fetch del Static Maps al abrirla.
    ...(properties?.some((p) => p.lat && p.lng)
      ? [
          {
            id: "map",
            label: "Mapa",
            bg: null,
            slots: [],
            type: "map",
            showHeader: true,
          },
        ]
      : []),
    // Comparativa: 1 página por chunk de 6 props.
    ...Array.from({ length: compPagesCount }, (_, idx) => ({
      id: `comp-${idx}`,
      label: compPagesCount > 1 ? `Comparativa ${idx + 1}` : "Comparativa",
      bg: null,
      slots: [],
      type: "comparative",
      comparativeChunkIndex: idx,
      showHeader: true,
    })),
    {
      id: "price",
      label: "Precio",
      bg: brand?.additional_page1_url || null,
      type: "price",
      slots: [],
      showHeader: true,
    },
  ];

  // Página adicional 3 — opcional, entre Precio y Documentación
  if (brand?.additional_page3_url) {
    pages.push({
      id: "extra3",
      label: "Página adicional",
      bg: brand.additional_page3_url,
      slots: [],
      showHeader: true,
      isExtra: true,
    });
  }

  pages.push({
    id: "docs",
    label: "Documentación",
    bg: brand?.additional_page2_url || DOCUMENTACION,
    slots: [
      {
        id: "commissionPercent",
        xMm: PAGE_W_MM * 0.345,
        yMm: 180.5,
        align: "center",
        fontPt: 17,
        widthMm: 30,
      },
    ],
    showHeader: true,
  });

  // Página adicional 4 — opcional, entre Documentación y Contacto
  if (brand?.additional_page4_url) {
    pages.push({
      id: "extra4",
      label: "Página adicional",
      bg: brand.additional_page4_url,
      slots: [],
      showHeader: true,
      isExtra: true,
    });
  }

  pages.push({
    id: "contact",
    label: "Contacto",
    bg: null,
    slots: [],
    type: "contact",
    showHeader: false,
  });

  return pages;
};

const PDFPreviewEditor = ({
  visible,
  onHide,
  reportName,
  setReportName,
  reportPrices = [],
  onReportPriceChange,
  reportContact,
  onReportContactChange,
  onContactDefaultsChange,
  commissionPercent,
  setCommissionPercent,
  currencyMode = "USD",
  loadingReport,
  onGeneratePdf,
  onGenerateExcel,
  // Datos para el preview no-editable
  wizardData,
  // Propiedades activas (las que entran al informe) — para el preview del
  // mapa, las páginas comparativa y el contexto del chat Hornero.
  properties = [],
  // Setter para enviar las páginas ocultas al PropertyReport — desde ahí
  // se pasan a generatePDF para que el PDF las respete.
  onHiddenPagesChange,
  // Setter para que las páginas custom suban al padre y entren al PDF.
  onCustomPagesChange,
  // Flujo de generación: estados + handlers para el overlay grande que se
  // muestra durante "generando → subiendo → listo / error".
  flowStep = "idle",
  flowError = null,
  uploadedFile = null,
  onFlowClose,
  onFlowReset,
}) => {
  const [activePageId, setActivePageId] = useState("cover");
  const [showPriceEditControls, setShowPriceEditControls] = useState(true);
  const [showContactEditControls, setShowContactEditControls] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const [documentNode, setDocumentNode] = useState(null);
  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  // IDs de páginas que el user decidió OCULTAR del PDF generado. Solo las
  // adicionales se pueden ocultar (cover/wizard/price/docs/contact son base).
  // Se pasa a generatePDF para que respete la decisión.
  const [hiddenPageIds, setHiddenPageIds] = useState([]);
  // Páginas vacías agregadas por el user. Cada una es { id, title, body }.
  // Se insertan entre Documentación y Contacto en el PDF. El user puede
  // escribir manualmente O pedirle al chat Hornero que escriba directo en
  // la página activa.
  const [customPages, setCustomPages] = useState([]);

  const addCustomPage = () => {
    const id = `custom-${Date.now()}`;
    setCustomPages((prev) => [
      ...prev,
      { id, title: "Página nueva", body: "" },
    ]);
    setActivePageId(id);
  };

  const updateCustomPage = (id, patch) => {
    setCustomPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  };

  const removeCustomPage = (id) => {
    setCustomPages((prev) => prev.filter((p) => p.id !== id));
    setActivePageId("price"); // volver a una página base
  };

  // Catálogo dinámico: cuando llega el branding, las páginas con custom
  // bg/extra se reconstruyen. Las páginas vacías agregadas por el user
  // (customPages) se insertan después de las del branding, antes de Contacto.
  const PAGES = useMemo(() => {
    const base = buildPages(branding || {}, properties, wizardData);
    if (customPages.length === 0) return base;
    // Insertar customPages antes de la página Contacto (última posición).
    const out = [...base];
    const contactIdx = out.findIndex((p) => p.id === "contact");
    const insertAt = contactIdx === -1 ? out.length : contactIdx;
    const customAsPages = customPages.map((cp) => ({
      id: cp.id,
      label: cp.title || "Página nueva",
      bg: null, // fondo blanco; el header brand se renderiza encima
      slots: [],
      type: "custom",
      showHeader: true,
      isCustom: true, // distinto a isExtra (que es las del branding)
      customData: cp,
    }));
    out.splice(insertAt, 0, ...customAsPages);
    return out;
  }, [branding, customPages, properties, wizardData]);
  const activePage = useMemo(
    () => PAGES.find((p) => p.id === activePageId) || PAGES[0],
    [PAGES, activePageId],
  );

  const pricesComplete = areReportPricesComplete(reportPrices);
  const editableCount = activePage.type === "price"
    ? 4
    : activePage.type === "contact" ? 6
    : activePage.slots.filter((slot) => !slot.readonlyMirror).length;
  const contact = useMemo(
    () => reportContact || resolveReportContact(branding),
    [reportContact, branding],
  );
  useEffect(() => {
    if (branding) onContactDefaultsChange?.(resolveReportContact(branding));
  }, [branding, onContactDefaultsChange]);

  // Cada apertura comienza en la portada y conserva las ediciones del informe.
  useLayoutEffect(() => {
    if (visible) setActivePageId("cover");
  }, [visible]);

  // Cargo branding cuando se abre el dialog (no antes — ahorra requests).
  useEffect(() => {
    if (!visible || branding || brandingLoading) return;
    setBrandingLoading(true);
    fetchBranding()
      .then((b) => setBranding(b))
      .catch(() => setBranding({}))
      .finally(() => setBrandingLoading(false));
  }, [visible, branding, brandingLoading]);

  // Subo las páginas ocultas al padre cada vez que cambian (para que el
  // PDF respete la decisión del user).
  useEffect(() => {
    onHiddenPagesChange?.(hiddenPageIds);
  }, [hiddenPageIds, onHiddenPagesChange]);

  // Y subo también las páginas custom para que se rendericen en el PDF.
  useEffect(() => {
    onCustomPagesChange?.(customPages);
  }, [customPages, onCustomPagesChange]);

  // Escalar la hoja sin alterar las coordenadas que usa el PDF.
  useLayoutEffect(() => {
    if (!documentNode) return;
    const resize = () => setPreviewScale(Math.min(1, documentNode.clientWidth / PREVIEW_W_PX));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(documentNode);
    return () => observer.disconnect();
  }, [documentNode]);

  const slotBindings = {
    commissionPercent: {
      value: commissionPercent,
      setValue: setCommissionPercent,
    },
  };

  const handleSlotChange = (slot, raw) => {
    const binding = slotBindings[slot.id];
    if (!binding) return;
    const norm = raw.replace(",", ".");
    if (norm === "" || /^\d*\.?\d{0,2}$/.test(norm)) binding.setValue(norm);
  };

  // Comisiones comunes para asignar rápido. Si el user quiere otra, edita
  // el input in-place en la página de Documentación.
  const COMMON_COMMISSIONS = [3, 4, 5, 6, 7, 10];

  return (
    <Dialog
      header={<ReportModalHeader />}
      visible={visible}
      modal
      blockScroll
      dismissableMask={false}
      /* Bloqueamos cierre mientras está generando o subiendo. En 'done' o
         'error' el user cierra con el botón del overlay. */
      closable={flowStep === "idle"}
      closeOnEscape={flowStep === "idle"}
      onHide={() => flowStep === "idle" && onHide()}
      className="report-preview-dialog"
      style={{ width: "98vw", maxWidth: 1680, height: "96vh", maxHeight: "96vh", position: "relative" }}
      contentStyle={{ padding: 0, background: "#F8FAFC", height: "100%", maxHeight: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
      breakpoints={{ "1100px": "96vw", "640px": "100vw" }}
    >
      {/* Overlay grande del flujo de generación. Cubre el editor completo
          mientras se genera el PDF, se sube al storage, y al final muestra
          los botones de compartir con la URL real. Antes el upload corría
          en background sin feedback y a veces no terminaba sin que el user
          se enterara. */}
      {flowStep !== "idle" && (
        <FlowOverlay
          step={flowStep}
          error={flowError}
          uploadedFile={uploadedFile}
          reportName={reportName}
          onClose={onFlowClose}
          onRetry={onFlowReset}
          onGeneratePdf={onGeneratePdf}
          onGenerateExcel={onGenerateExcel}
        />
      )}

      <div className="report-preview-actions-bar">
        <div className="report-preview-actions-bar__file">
          <span>Nombre del archivo</span>
          <InputText
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            disabled={loadingReport}
            placeholder="Nombre del archivo"
            className="report-preview-actions-bar__input"
          />
        </div>
        <div className="report-preview-actions-bar__buttons">
          <Button
            label="Hornero"
            icon={chatOpen ? "pi pi-times" : "pi pi-comments"}
            className="report-preview-chat-toggle"
            size="small"
            outlined
            aria-expanded={chatOpen}
            aria-controls="report-preview-chat"
            onClick={() => setChatOpen((open) => !open)}
          />
          <Button
            label="Generar PDF"
            icon="pi pi-file-pdf"
            severity="danger"
            disabled={loadingReport || !pricesComplete}
            onClick={onGeneratePdf}
            size="small"
          />
          <Button
            label="Excel"
            icon="pi pi-file-excel"
            disabled={loadingReport}
            onClick={onGenerateExcel}
            size="small"
            outlined
            className="report-preview-btn-excel"
          />
        </div>
      </div>

      {!pricesComplete && (
        <div className="report-preview-price-validation" role="status">
          Completá las cuatro alternativas con precios mayores a cero para generar el PDF.
        </div>
      )}
      {/* Cuerpo: sidebar + preview */}
      <div className="report-preview-workspace" style={{ display: "flex", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
        <aside
          style={{
            width: 180,
            flexShrink: 0,
            background: "#fff",
            borderRight: "1px solid #E2E8F0",
            padding: "10px 0",
            overflowY: "auto",
            height: "100%",
            maxHeight: "100%",
            minHeight: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              padding: "6px 14px",
            }}
          >
            Páginas
          </div>
          {PAGES.map((page, idx) => {
            const active = page.id === activePageId;
            const editable = page.type === "price" || page.type === "contact" || page.slots.length > 0 || page.isCustom;
            const hidden = hiddenPageIds.includes(page.id);
            // Solo páginas adicionales (del branding) y custom (creadas acá)
            // se pueden ocultar/eliminar. Las base no.
            const canHide = page.isExtra || page.isCustom;
            return (
              <div
                key={page.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  background: active && !hidden ? "#FFF8F4" : "transparent",
                  borderLeft:
                    active && !hidden
                      ? "3px solid #FA7317"
                      : "3px solid transparent",
                  opacity: hidden ? 0.55 : 1,
                  paddingRight: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() => setActivePageId(page.id)}
                  disabled={hidden}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flex: 1,
                    padding: "8px 14px",
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    cursor: hidden ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    if (!active && !hidden)
                      e.currentTarget.parentElement.style.background =
                        "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (!active && !hidden)
                      e.currentTarget.parentElement.style.background =
                        "transparent";
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: active && !hidden ? "#FA7317" : "#E2E8F0",
                      color: active && !hidden ? "#fff" : "#475569",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                      textDecoration: hidden ? "line-through" : "none",
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 12,
                      color: "#0F172A",
                      fontWeight: active && !hidden ? 700 : 500,
                      textDecoration: hidden ? "line-through" : "none",
                    }}
                  >
                    {page.label}
                  </span>
                  {editable && !hidden && (
                    <i
                      className="pi pi-pencil"
                      style={{
                        fontSize: 9,
                        color: active ? "#FA7317" : "#94A3B8",
                      }}
                      title="Tiene campos editables"
                    />
                  )}
                  {hidden && (
                    <span
                      style={{
                        fontSize: 8,
                        color: "#dc2626",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                      }}
                    >
                      Oculta
                    </span>
                  )}
                </button>
                {/* X — para custom ELIMINA (no se restauran), para extra
                    OCULTA con toggle (se pueden restaurar). */}
                {canHide && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (page.isCustom) {
                        removeCustomPage(page.id);
                      } else {
                        setHiddenPageIds((prev) =>
                          hidden
                            ? prev.filter((id) => id !== page.id)
                            : [...prev, page.id],
                        );
                      }
                    }}
                    title={
                      page.isCustom
                        ? "Eliminar página"
                        : hidden
                          ? "Restaurar página"
                          : "Ocultar del informe"
                    }
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 4,
                      color: hidden ? "#FA7317" : "#94A3B8",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={hidden ? "pi pi-replay" : "pi pi-times"}
                      style={{ fontSize: 11 }}
                    />
                  </button>
                )}
              </div>
            );
          })}
          {/* Botón "+ Agregar página" — crea una página vacía editable.
              El user escribe ahí o le pide al chat Hornero que escriba. */}
          <button
            type="button"
            onClick={addCustomPage}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "calc(100% - 6px)",
              margin: "8px 0 0 3px",
              padding: "8px 14px",
              border: "1px dashed #CBD5E1",
              borderRadius: 6,
              background: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              color: "#475569",
              fontSize: 12,
              fontWeight: 600,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#FA7317";
              e.currentTarget.style.color = "#C2410C";
              e.currentTarget.style.background = "#FFF8F4";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#CBD5E1";
              e.currentTarget.style.color = "#475569";
              e.currentTarget.style.background = "transparent";
            }}
          >
            <i className="pi pi-plus" style={{ fontSize: 10 }} />
            Agregar página
          </button>
        </aside>

        <main
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: 14,
            overflow: "auto",
            height: "100%",
            maxHeight: "100%",
            minHeight: 0,
          }}
        >
          <div className="report-preview-document" ref={setDocumentNode}>
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                marginBottom: 8,
                textAlign: "center",
                fontWeight: 600,
              }}
            >
              Página {PAGES.findIndex((p) => p.id === activePageId) + 1} de{" "}
              {PAGES.length}
              {editableCount > 0 && (
                <span style={{ marginLeft: 8, color: "#E06312" }}>
                  · {editableCount}{" "}
                  {editableCount === 1
                    ? "campo editable"
                    : "campos editables"}
                </span>
              )}
            </div>
            <div className="report-preview-sheet-wrap">
            <div className="report-preview-paper" style={{ width: PREVIEW_W_PX * previewScale, height: PREVIEW_H_PX * previewScale }}>
            <div
              className="report-preview-sheet"
              style={{
                position: "relative",
                transform: `scale(${previewScale})`,
                transformOrigin: "top left",
                width: PREVIEW_W_PX,
                height: PREVIEW_H_PX,
                background: "#fff",
                boxShadow: "0 4px 24px rgba(15, 23, 42, 0.10)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {activePage.bg && (
                <img
                  src={activePage.bg}
                  alt={activePage.label}
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "fill",
                  }}
                />
              )}

              {/* Header — barras brand + logo de empresa. En el PDF lo hace
                  addHeaderFooter() + drawBRLogoSync() en todas las páginas
                  excepto Cover y Contacto. */}
              {activePage.showHeader && <PageHeader branding={branding} />}

              {activePage.type === "price" && (
                <ReportPriceSuggestions
                  prices={reportPrices}
                  currency={currencyMode}
                  onPriceChange={onReportPriceChange}
                  showEditControls={showPriceEditControls}
                />
              )}

              {/* Overlays no editables — datos del wizard o agente */}
              {activePage.type === "wizard" && (
                <WizardOverlay wizardData={wizardData} branding={branding} />
              )}
              {activePage.type === "contact" && (
                <ReportContactPage
                  contact={contact}
                  branding={branding}
                  onChange={onReportContactChange}
                  showEditControls={showContactEditControls}
                />
              )}
              {activePage.type === "map" && (
                <MapOverlay
                  properties={properties}
                  wizardData={wizardData}
                  branding={branding}
                />
              )}
              {activePage.type === "comparative" && (
                <ComparativeOverlay
                  properties={properties}
                  chunkIndex={activePage.comparativeChunkIndex}
                  branding={branding}
                />
              )}
              {/* Página custom: título + textarea grande para que el user
                  escriba (o pegue lo que Hornero generó). */}
              {activePage.type === "custom" && (
                <CustomPageEditor
                  page={activePage.customData}
                  onUpdate={(patch) => updateCustomPage(activePage.id, patch)}
                />
              )}

              {/* Slots editables — inputs flotando con tipografía REAL del PDF */}
              {activePage.slots.map((slot) => {
                const binding = slotBindings[slot.id];
                const displayValue = binding?.value ?? "";

                // Tipografía real del PDF: pt → px del preview con la conversión correcta.
                const fontPx = slot.fontPt * PT_TO_PX;
                const inputWPx = slot.widthMm * MM_TO_PX;
                const xPx = slot.xMm * MM_TO_PX;
                const yPx = slot.yMm * MM_TO_PX;

                // jsPDF posiciona texto por baseline. CSS por top. Ajuste:
                // baseline ≈ top + 0.78 * fontSize (Helvetica). Por eso top = y - 0.78 * fontPx.
                const leftPx =
                  slot.align === "center" ? xPx - inputWPx / 2 : xPx;
                const topPx = yPx - fontPx * 0.78;

                return (
                  <input
                    key={slot.id}
                    type="text"
                    inputMode="decimal"
                    aria-label="Comisión del informe"
                    value={
                      slot.prefix && displayValue
                        ? `${slot.prefix}${displayValue}`
                        : displayValue
                    }
                    readOnly={Boolean(slot.readonlyMirror)}
                    onChange={(e) => {
                      // Si tiene prefix, lo quitamos antes de guardar.
                      let v = e.target.value;
                      if (slot.prefix && v.startsWith(slot.prefix)) {
                        v = v.slice(slot.prefix.length);
                      }
                      handleSlotChange(slot, v);
                    }}
                    placeholder=""
                    style={{
                      position: "absolute",
                      left: leftPx,
                      top: topPx,
                      width: inputWPx,
                      padding: 0,
                      margin: 0,
                      border: "1px solid transparent",
                      borderRadius: 2,
                      background: "transparent",
                      color: "#545454", // mismo color que setTextColor(84,84,84) del PDF
                      fontSize: fontPx,
                      fontWeight: "normal",
                      textAlign: slot.align,
                      outline: "none",
                      fontFamily: "Helvetica, Arial, sans-serif",
                      lineHeight: 1,
                      cursor: slot.readonlyMirror ? "default" : "text",
                    }}
                    onFocus={(e) => {
                      e.target.style.border = "1px solid #FA7317";
                      e.target.style.background = "rgba(255,255,255,0.7)";
                    }}
                    onBlur={(e) => {
                      e.target.style.border = "1px solid transparent";
                      e.target.style.background = "transparent";
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.border = "1px dashed #FA7317";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.border = "1px solid transparent";
                      }
                    }}
                  />
                );
              })}
            </div>
            </div>
            {activePage.id === "docs" && (
              <section className="report-commission-suggestions" aria-label="Sugerencias de comisión">
                <h3>¿Qué comisión querés incluir?</h3>
                <p>Elegí un porcentaje o escribilo directamente en la hoja.</p>
                <div className="report-commission-suggestions__options">
                  {COMMON_COMMISSIONS.map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      aria-pressed={Number(commissionPercent) === pct}
                      onClick={() => setCommissionPercent(String(pct))}
                    >{pct}%</button>
                  ))}
                </div>
              </section>
            )}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "#94A3B8",
                marginTop: 10,
                textAlign: "center",
                maxWidth: PREVIEW_W_PX,
              }}
            >
              {activePage.type === "price" ? (
                <div className="report-price-edit-tools">
                  <span>Los cuatro precios se incluyen en el PDF. Hacé clic en un importe para editarlo.</span>
                  <button
                    type="button"
                    onClick={() => setShowPriceEditControls((show) => !show)}
                  >
                    {showPriceEditControls ? "Ver sin marcas de edición" : "Editar precios"}
                  </button>
                </div>
              ) : activePage.type === "contact" ? (
                <div className="report-price-edit-tools">
                  <span>Hacé clic en tus datos para editarlos en este informe.</span>
                  <ReportContactTools
                    photoUrl={contact.photoUrl}
                    onPhotoChange={(value) => onReportContactChange("photoUrl", value)}
                    showEditControls={showContactEditControls}
                    onToggle={() => setShowContactEditControls((show) => !show)}
                  />
                </div>
              ) : (
                "Lo que escribís acá aparece en la misma posición y tipografía en el PDF final."
              )}
            </div>
          </div>
        </main>

        {/* Panel lateral del chat Hornero — SIEMPRE abierto. Si la página
            activa es CUSTOM, muestra el botón "Insertar en la página" que
            escribe el texto generado en el body de esa página directamente.
            Le pasamos las properties para que el backend pueda inyectarlas
            al system prompt → Hornero puede armar tablas comparativas
            con los datos reales de los comparables del informe. */}
        <div id="report-preview-chat" className={`report-preview-chat${chatOpen ? " is-open" : ""}`}>
        <HorneroChat
          wizardData={wizardData}
          properties={properties}
          onInsertToPage={
            activePage.type === "custom"
              ? (text) => {
                  // Si el texto generado empieza con un "# Título" en la primera
                  // línea, lo extraemos al SLOT de título de la página custom
                  // y dejamos el resto como body. Antes el título quedaba dentro
                  // del body y el user tenía que copiarlo a mano al título real.
                  const lines = text.split("\n");
                  let titleLine = "";
                  let bodyStart = 0;
                  // Saltamos líneas vacías iniciales y buscamos un "# heading".
                  while (bodyStart < lines.length && !lines[bodyStart].trim())
                    bodyStart++;
                  const first = (lines[bodyStart] || "").trim();
                  const headingMatch = first.match(/^#{1,3}\s+(.+)$/);
                  if (headingMatch) {
                    titleLine = headingMatch[1].trim();
                    bodyStart += 1;
                    // Comer también una línea en blanco después del heading.
                    while (bodyStart < lines.length && !lines[bodyStart].trim())
                      bodyStart++;
                  }
                  const body = lines.slice(bodyStart).join("\n").trim();
                  const patch = { body };
                  if (titleLine) patch.title = titleLine;
                  updateCustomPage(activePage.id, patch);
                }
              : null
          }
          activePageTitle={
            activePage.type === "custom" ? activePage.label : null
          }
        />
        </div>
      </div>
    </Dialog>
  );
};

const ReportModalHeader = () => (
  <div className="report-preview-modal-header">
    <span className="report-preview-modal-header__badge">Informe de tasación</span>
    <div className="report-preview-modal-header__content">
      <h2>Editá tu informe</h2>
      <div className="report-preview-modal-header__branding">
        <a href="https://panel.cotify.com.ar/settings" target="_blank" rel="noopener noreferrer">
          <i className="pi pi-palette" aria-hidden="true" /> Personalizar marca y datos del asesor
        </a>
      </div>
    </div>
  </div>
);

// ─── Header de página (barras brand + logo empresa) ────────────────────────
// Replica addHeaderFooter (pdfUtils.js:223-231) + drawBRLogoSync (pdfUtils.js:162-169).
const PageHeader = ({ branding }) => {
  const brandColor = branding?.primary_color || "#FA7317";
  const logoUrl = branding?.logo_url;

  // Barra arriba: 3mm. Barra abajo: 10mm.
  const barTopHpx = 3 * MM_TO_PX;
  const barBottomHpx = 10 * MM_TO_PX;
  // Logo BR: 22x20mm con margin 10mm desde bottom-right.
  const logoBoxW = 22 * MM_TO_PX;
  const logoBoxH = 20 * MM_TO_PX;
  const logoMargin = 10 * MM_TO_PX;

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: barTopHpx,
          background: brandColor,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: barBottomHpx,
          background: brandColor,
        }}
      />
      {logoUrl && (
        <img
          src={logoUrl}
          alt="logo"
          style={{
            position: "absolute",
            right: logoMargin,
            bottom: logoMargin + barBottomHpx,
            width: logoBoxW,
            height: logoBoxH,
            objectFit: "contain",
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
    </div>
  );
};

// ─── Overlay para la página WIZARD (Datos buscados) ──────────────────────────
// Replica el render de pdfUtils.js:439-523. Coords en mm, fuente Helvetica.
const WizardOverlay = ({ wizardData, branding }) => {
  // CRÍTICO: normalizeWizard mapea las variantes camelCase del wizard
  // (typeProperty/totalArea/coveredArea) a las snake_case que usa el resto
  // del código (type_property/square_meters_total/square_meters_covered).
  // Sin esto, el preview mostraba N/A mientras el PDF (que sí normaliza)
  // mostraba los valores reales.
  const W = normalizeWizard(wizardData || {});
  const address =
    W.selectedPlace?.formatted_address || "Dirección no especificada";

  const propertyData = [
    {
      label: "Tipo de propiedad",
      value: typePropertyMap[W.type_property] || humanizeType(W.type_property),
    },
    {
      label: "Superficie total",
      value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[4], " m²"),
    },
    {
      label: "Superficie cubierta",
      value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[3], " m²"),
    },
    { label: "Cochera", value: (W.parking ?? "N/A").toString() },
    {
      label: "Baños",
      value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[2]),
    },
    {
      label: "Ambientes",
      value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[0]),
    },
    {
      label: "Dormitorios",
      value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[1]),
    },
  ];

  const brandColor = branding?.primary_color || "#FA7317";
  const column1XPx = 52 * MM_TO_PX;
  const column2XPx = 210 * MM_TO_PX;
  const valueOffsetPx = 50 * MM_TO_PX;
  const yStartPx = 103 * MM_TO_PX;
  const lineHeightPx = 18.9 * MM_TO_PX;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: PREVIEW_W_PX,
        height: PREVIEW_H_PX,
        pointerEvents: "none",
        fontFamily: "Helvetica, Arial, sans-serif",
        color: "#737373", // setTextColor(115,115,115)
      }}
    >
      {/* Línea decorativa brand */}
      <div
        style={{
          position: "absolute",
          left: 40 * MM_TO_PX,
          top: 80 * MM_TO_PX,
          width: 10 * MM_TO_PX,
          height: 2,
          background: brandColor,
        }}
      />
      {/* Dirección */}
      <div
        style={{
          position: "absolute",
          left: 45 * MM_TO_PX,
          top: 60 * MM_TO_PX - 23 * PT_TO_PX * 0.78,
          width: 200 * MM_TO_PX,
          fontSize: 23 * PT_TO_PX,
          lineHeight: 1.15,
          wordBreak: "break-word",
        }}
      >
        {address}
      </div>

      {/* Tabla 2 columnas — mismo render del PDF (label en columnX, valor
          en columnX + 50mm). */}
      {propertyData.map((item, i) => {
        const isCol2 = i >= 4;
        const colX = isCol2 ? column2XPx : column1XPx;
        const itemIdx = isCol2 ? i - 4 : i;
        const y = yStartPx + itemIdx * lineHeightPx;
        return (
          <React.Fragment key={i}>
            <div
              style={{
                position: "absolute",
                left: colX,
                top: y - 14 * PT_TO_PX * 0.78,
                fontSize: 14 * PT_TO_PX,
                fontWeight: 700,
              }}
            >
              {item.label}:
            </div>
            <div
              style={{
                position: "absolute",
                left: colX + valueOffsetPx,
                top: y - 13 * PT_TO_PX * 0.78,
                fontSize: 13 * PT_TO_PX,
              }}
            >
              {item.value}
            </div>
          </React.Fragment>
        );
      })}

      {/* Comodidades */}
      <div
        style={{
          position: "absolute",
          left: column1XPx,
          top:
            yStartPx + 3 * lineHeightPx + 52 * MM_TO_PX - 14 * PT_TO_PX * 0.78,
          fontSize: 14 * PT_TO_PX,
          fontWeight: 700,
        }}
      >
        Comodidades:
      </div>
      <div
        style={{
          position: "absolute",
          left: column1XPx + 35 * MM_TO_PX,
          top:
            yStartPx + 3 * lineHeightPx + 52 * MM_TO_PX - 13 * PT_TO_PX * 0.78,
          width: 200 * MM_TO_PX,
          fontSize: 13 * PT_TO_PX,
        }}
      >
        {formatAmenities(W)}
      </div>
    </div>
  );
};

// ─── Parser markdown simple para el preview ──────────────────────────────
// Soporta: # / ## / ### headings, - / * bullets, **negrita**, *itálica*,
// tablas markdown (| col | col |). Suficiente para textos generados por
// Hornero. Se renderiza como JSX para el preview Y el formato matchea con
// lo que renderFormattedText hace en el PDF.

const renderInlineMarkdown = (text) => {
  // **negrita** y *itálica*. Hacemos un parse simple por regex.
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// Estilo "Cotify Open Sans #545454" — usado en páginas custom para matchear
// el branding que pidió el cliente.
const CUSTOM_FONT_FAMILY = '"Open Sans", "Helvetica", Arial, sans-serif';
const CUSTOM_TEXT_COLOR = "#545454";

const MarkdownPreview = ({ text, baseFontPx }) => {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Headings
    const h = line.match(/^(#{1,3})\s+(.*)/);
    if (h) {
      const level = h[1].length;
      const fontSize =
        level === 1
          ? baseFontPx * 1.5
          : level === 2
            ? baseFontPx * 1.3
            : baseFontPx * 1.1;
      blocks.push(
        <div
          key={key++}
          style={{
            fontSize,
            fontWeight: 700,
            color: CUSTOM_TEXT_COLOR,
            fontFamily: CUSTOM_FONT_FAMILY,
            margin: "6px 0 3px",
          }}
        >
          {renderInlineMarkdown(h[2])}
        </div>,
      );
      i++;
      continue;
    }
    // Tablas markdown
    if (
      line.match(/^\s*\|.*\|\s*$/) &&
      i + 1 < lines.length &&
      lines[i + 1].match(/^\s*\|[-:\s|]+\|\s*$/)
    ) {
      const headerCells = line
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].match(/^\s*\|.*\|\s*$/)) {
        rows.push(
          lines[i]
            .split("|")
            .slice(1, -1)
            .map((c) => c.trim()),
        );
        i++;
      }
      blocks.push(
        <table
          key={key++}
          style={{
            width: "100%",
            borderCollapse: "collapse",
            margin: "6px 0",
            fontSize: baseFontPx * 0.92,
            color: CUSTOM_TEXT_COLOR,
            fontFamily: CUSTOM_FONT_FAMILY,
          }}
        >
          <thead>
            <tr style={{ background: "#F1F5F9" }}>
              {headerCells.map((c, j) => (
                <th
                  key={j}
                  style={{
                    padding: "4px 6px",
                    border: "1px solid #CBD5E1",
                    textAlign: "left",
                    fontWeight: 700,
                    color: CUSTOM_TEXT_COLOR,
                  }}
                >
                  {renderInlineMarkdown(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((c, ci) => (
                  <td
                    key={ci}
                    style={{
                      padding: "3px 6px",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    {renderInlineMarkdown(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }
    // Bullets
    if (line.match(/^\s*[-*]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*[-*]\s+/)) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul
          key={key++}
          style={{
            margin: "4px 0",
            paddingLeft: 18,
            fontSize: baseFontPx,
            color: CUSTOM_TEXT_COLOR,
            fontFamily: CUSTOM_FONT_FAMILY,
            lineHeight: 1.45,
          }}
        >
          {items.map((it, j) => (
            <li key={j}>{renderInlineMarkdown(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    if (line.trim() === "") {
      blocks.push(<div key={key++} style={{ height: baseFontPx * 0.5 }} />);
      i++;
      continue;
    }
    blocks.push(
      <div
        key={key++}
        style={{
          fontSize: baseFontPx,
          color: CUSTOM_TEXT_COLOR,
          fontFamily: CUSTOM_FONT_FAMILY,
          lineHeight: 1.45,
          margin: "2px 0",
        }}
      >
        {renderInlineMarkdown(line)}
      </div>,
    );
    i++;
  }
  return <>{blocks}</>;
};

// ─── Overlay para la página de MAPA ─────────────────────────────────────────
// Replica el render del PDF (pdfUtils.js:716-775): título + línea brand +
// imagen Google Static Maps con un marker por propiedad y uno azul "S" para
// el target. Mismo URL que el PDF — si está cacheado en el browser, instant.
const MapOverlay = ({ properties, wizardData, branding }) => {
  const brandColor = branding?.primary_color || "#FA7317";
  const valid = (properties || []).filter((p) => p.lat && p.lng);
  const W = normalizeWizard(wizardData || {});
  const subjectLat = Number(W?.selectedPlace?.lat);
  const subjectLng = Number(W?.selectedPlace?.lng);
  const hasSubject = Number.isFinite(subjectLat) && Number.isFinite(subjectLng);

  // URL del mapa — solo se calcula si hay propiedades; sino mostramos un
  // placeholder.
  const mapUrl = useMemo(() => {
    if (valid.length === 0) return null;
    const pointsForCenter = [...valid];
    if (hasSubject) pointsForCenter.push({ lat: subjectLat, lng: subjectLng });
    const center = calculateMapCenter(pointsForCenter);
    const zoom = calculateZoomLevel(pointsForCenter);
    const subjectMarker = hasSubject
      ? `markers=color:blue%7Clabel:S%7C${subjectLat},${subjectLng}`
      : null;
    const compMarkers = valid
      .map((p, i) => `markers=color:${portalColors(p)[0].replace('#', '0x')}%7Clabel:${i + 1}%7C${p.lat},${p.lng}`)
      .join("&");
    const markersParam = subjectMarker
      ? `${subjectMarker}&${compMarkers}`
      : compMarkers;
    return (
      `https://maps.googleapis.com/maps/api/staticmap` +
      `?center=${center.lat},${center.lng}&zoom=${zoom}` +
      `&size=600x600&scale=2&maptype=roadmap&${markersParam}` +
      `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    );
  }, [valid, hasSubject, subjectLat, subjectLng]);

  // Título + línea brand (mismo lugar que el PDF: 20mm de la izquierda, 30mm
  // del top). Imagen del mapa: 180×180mm centrada.
  const titleX = 20 * MM_TO_PX;
  const titleY = 30 * MM_TO_PX;
  const lineY = 34 * MM_TO_PX;
  const imgW = 180 * MM_TO_PX;
  const imgH = 180 * MM_TO_PX;
  const imgX = (PREVIEW_W_PX - imgW) / 2;
  const imgY = (PREVIEW_H_PX - imgH) / 2;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        fontFamily: "Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: titleX,
          top: titleY - 16 * PT_TO_PX * 0.78,
          fontSize: 16 * PT_TO_PX,
          fontWeight: 700,
          color: "#323232",
        }}
      >
        Ubicación de las Propiedades
      </div>
      <div
        style={{
          position: "absolute",
          left: titleX,
          top: lineY,
          width: 21 * MM_TO_PX,
          height: 2,
          background: brandColor,
        }}
      />
      {mapUrl ? (
        <img
          src={mapUrl}
          alt="Mapa"
          style={{
            position: "absolute",
            left: imgX,
            top: imgY,
            width: imgW,
            height: imgH,
            background: "#F1F5F9",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            left: imgX,
            top: imgY,
            width: imgW,
            height: imgH,
            background: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94A3B8",
            fontSize: 14,
          }}
        >
          Sin propiedades con coordenadas válidas
        </div>
      )}
    </div>
  );
};

// ─── Overlay para páginas COMPARATIVA ──────────────────────────────────────
// Replica el render del PDF (pdfUtils.js:777-960): 1 página por cada 6
// propiedades. Tabla con TODAS las filas del PDF (23 filas):
//   Imagen, Link, Google Maps, Precio, Expensas, Valor por m², Dirección,
//   Sup. Total, Sup. Cubierta, Ambientes, Dormitorios, Baños, Jardín, Pileta,
//   Parrilla, Terraza, Cocheras, Antigüedad, Operación, Propiedad,
//   Días Publicado, Visualizaciones, Inmobiliaria.
const ComparativeOverlay = ({ properties, chunkIndex, branding }) => {
  const brandColor = branding?.primary_color || "#FA7317";
  const chunk = (properties || []).slice(chunkIndex * 6, (chunkIndex + 1) * 6);

  const titleX = 20 * MM_TO_PX;
  const titleY = 30 * MM_TO_PX;
  const lineY = 34 * MM_TO_PX;
  const tableX = 14 * MM_TO_PX;
  const tableY = 44 * MM_TO_PX;
  const tableW = PAGE_W_MM * MM_TO_PX - tableX * 2;

  const fmtMoney = (cur, val) =>
    val != null && val !== ""
      ? `${cur || "USD"} ${Number(val).toLocaleString("es-AR")}`
      : "—";
  const yesNo = (v) => (v ? "Sí" : "No");
  const dash = (v) => (v == null || v === "" ? "—" : v);

  // Filas exactas del PDF — en el mismo orden.
  const rowsDef = [
    {
      label: "Imagen",
      isImage: true,
      cell: (p) =>
        p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            style={{
              width: "100%",
              height: 18 * MM_TO_PX,
              objectFit: "cover",
              borderRadius: 2,
            }}
          />
        ) : (
          <div style={{ height: 18 * MM_TO_PX, background: "#F1F5F9" }} />
        ),
    },
    {
      label: "Link a la propiedad",
      cell: (p) =>
        p.url ? (
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0000FF",
              textDecoration: "underline",
              pointerEvents: "auto",
            }}
          >
            Link
          </a>
        ) : (
          "N/A"
        ),
    },
    {
      label: "Google Maps",
      cell: (p) =>
        p.lat && p.lng ? (
          <a
            href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0000FF",
              textDecoration: "underline",
              pointerEvents: "auto",
            }}
          >
            Ver Mapa
          </a>
        ) : (
          "N/A"
        ),
    },
    { label: "Precio", cell: (p) => fmtMoney(p.price_currency, p.price) },
    {
      label: "Expensas",
      cell: (p) => fmtMoney(p.expenses_currency, p.expenses_value),
    },
    {
      label: "Valor por m²",
      cell: (p) =>
        p.price && p.square_meters_total
          ? `${p.price_currency || "USD"} ${(p.price / p.square_meters_total).toFixed(2)}`
          : "—",
    },
    { label: "Dirección", cell: (p) => p.address || "—", multiline: true },
    {
      label: "Superficie Total",
      cell: (p) =>
        p.square_meters_total ? `${p.square_meters_total} m²` : "—",
    },
    {
      label: "Superficie Cubierta",
      cell: (p) =>
        p.square_meters_covered ? `${p.square_meters_covered} m²` : "—",
    },
    { label: "Ambientes", cell: (p) => dash(p.rooms) },
    { label: "Dormitorios", cell: (p) => dash(p.bedrooms) },
    { label: "Baños", cell: (p) => dash(p.bathrooms) },
    { label: "Jardín", cell: (p) => yesNo(p.hasGarden) },
    { label: "Pileta", cell: (p) => yesNo(p.hasPool) },
    { label: "Parrilla", cell: (p) => yesNo(p.hasGrill) },
    { label: "Terraza", cell: (p) => yesNo(p.hasTerrace) },
    { label: "Cocheras", cell: (p) => dash(p.parking) },
    {
      label: "Antigüedad",
      cell: (p) => (p.antiquity ? `${p.antiquity} años` : "—"),
    },
    { label: "Operación", cell: (p) => dash(p.typeOperation) },
    { label: "Propiedad", cell: (p) => dash(p.typeProperty) },
    {
      label: "Días Publicado",
      cell: (p) => (p.days_published != null ? p.days_published : "+"),
    },
    { label: "Visualizaciones", cell: (p) => dash(p.views) },
    { label: "Inmobiliaria", cell: (p) => dash(p.owner_type) + (p.publication_count > 1 ? ` · ${p.publication_count} anuncios / ${p.agency_count} inmobiliarias` : ""), multiline: true },
  ];

  const labelColW = 32 * MM_TO_PX;
  const propColW = (tableW - labelColW) / Math.max(1, chunk.length);
  // Fila estándar: 5mm. Imagen: 19mm. Multilínea (dirección, inmobiliaria): 9mm.
  const rowHeight = (row) => {
    if (row.isImage) return 19 * MM_TO_PX;
    if (row.multiline) return 9 * MM_TO_PX;
    return 5 * MM_TO_PX;
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        fontFamily: "Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: titleX,
          top: titleY - 16 * PT_TO_PX * 0.78,
          fontSize: 16 * PT_TO_PX,
          fontWeight: 700,
          color: "#323232",
        }}
      >
        Comparativa de Propiedades
      </div>
      <div
        style={{
          position: "absolute",
          left: titleX,
          top: lineY,
          width: 21 * MM_TO_PX,
          height: 2,
          background: brandColor,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: tableX,
          top: tableY,
          width: tableW,
          color: "#1a1a1a",
          border: "0.5px solid #CBD5E1",
        }}
      >
        {/* Header de columnas — Prop. 1, Prop. 2, ... */}
        <div
          style={{
            display: "flex",
            background: "#F1F5F9",
            borderBottom: `1.5px solid ${brandColor}`,
          }}
        >
          <div
            style={{
              width: labelColW,
              padding: "2px 4px",
              fontWeight: 700,
              fontSize: 7 * PT_TO_PX,
              color: "#0F172A",
              borderRight: "0.5px solid #CBD5E1",
            }}
          >
            Dato
          </div>
          {chunk.map((_, i) => (
            <div
              key={i}
              style={{
                width: propColW,
                padding: "2px 4px",
                fontWeight: 700,
                fontSize: 7 * PT_TO_PX,
                color: "#0F172A",
                borderRight: "0.5px solid #CBD5E1",
                textAlign: "center",
              }}
            >
              Prop. {chunkIndex * 6 + i + 1}
            </div>
          ))}
        </div>
        {/* Filas — alturas fijas para que entren las 23 en la página */}
        {rowsDef.map((row, ri) => (
          <div
            key={ri}
            style={{
              display: "flex",
              borderBottom: "0.5px solid #E2E8F0",
              background: ri % 2 === 0 ? "#fff" : "#F8FAFC",
              minHeight: rowHeight(row),
              alignItems: row.isImage ? "stretch" : "center",
            }}
          >
            <div
              style={{
                width: labelColW,
                padding: "1px 4px",
                fontWeight: 700,
                fontSize: 6.5 * PT_TO_PX,
                color: "#475569",
                borderRight: "0.5px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
              }}
            >
              {row.label}
            </div>
            {chunk.map((p, ci) => (
              <div
                key={ci}
                style={{
                  width: propColW,
                  padding: row.isImage ? "1px" : "1px 4px",
                  fontSize: 6.5 * PT_TO_PX,
                  borderRight: "0.5px solid #E2E8F0",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: row.isImage ? "stretch" : "center",
                }}
              >
                {row.cell(p)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Editor de página custom ────────────────────────────────────────────────
// Página en blanco con header brand + título editable + área de texto que
// alterna entre TEXTAREA (modo edición) y RENDER markdown (modo vista).
// Al hacer click en el área → modo edición. Al hacer click afuera → modo vista.
const CustomPageEditor = ({ page, onUpdate }) => {
  const padXMm = 20;
  const titleYMm = 25;
  const bodyYMm = 45;
  const bodyWMm = PAGE_W_MM - padXMm * 2;
  const bodyHMm = PAGE_H_MM - bodyYMm - 20;
  const baseFontPx = 12 * PT_TO_PX;
  const [editingBody, setEditingBody] = useState(false);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        fontFamily: CUSTOM_FONT_FAMILY,
        color: CUSTOM_TEXT_COLOR,
      }}
    >
      {/* Título — Open Sans 20pt bold #545454 */}
      <input
        type="text"
        value={page?.title || ""}
        onChange={(e) => onUpdate({ title: e.target.value })}
        placeholder="Título de la página"
        style={{
          position: "absolute",
          left: padXMm * MM_TO_PX,
          top: titleYMm * MM_TO_PX - 20 * PT_TO_PX * 0.78,
          width: bodyWMm * MM_TO_PX,
          fontSize: 20 * PT_TO_PX,
          fontWeight: 700,
          color: CUSTOM_TEXT_COLOR,
          border: "1px dashed transparent",
          background: "transparent",
          padding: 2,
          outline: "none",
          fontFamily: CUSTOM_FONT_FAMILY,
          pointerEvents: "auto",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#FA7317";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "transparent";
        }}
      />

      {/* Body: vista renderizada (markdown) o textarea editable */}
      {editingBody ? (
        <textarea
          value={page?.body || ""}
          onChange={(e) => onUpdate({ body: e.target.value })}
          onBlur={() => setEditingBody(false)}
          autoFocus
          placeholder={
            "Escribí el contenido acá. Aceptá markdown:\n\n# Título\n**Negrita** y *itálica*\n- Bullets\n\n| Col | Col |\n|-----|-----|\n| dato | dato |"
          }
          style={{
            position: "absolute",
            left: padXMm * MM_TO_PX,
            top: bodyYMm * MM_TO_PX,
            width: bodyWMm * MM_TO_PX,
            height: bodyHMm * MM_TO_PX,
            fontSize: baseFontPx,
            color: CUSTOM_TEXT_COLOR,
            border: "1px solid #FA7317",
            background: "#fff",
            padding: 6,
            outline: "none",
            fontFamily: "monospace", // mientras edita, monospace ayuda a ver el markdown
            lineHeight: 1.45,
            resize: "none",
            pointerEvents: "auto",
          }}
        />
      ) : (
        <div
          onClick={() => setEditingBody(true)}
          title="Click para editar"
          style={{
            position: "absolute",
            left: padXMm * MM_TO_PX,
            top: bodyYMm * MM_TO_PX,
            width: bodyWMm * MM_TO_PX,
            height: bodyHMm * MM_TO_PX,
            padding: 6,
            border: "1px dashed transparent",
            cursor: "text",
            pointerEvents: "auto",
            overflow: "auto",
            color: page?.body ? CUSTOM_TEXT_COLOR : "#94A3B8",
            fontFamily: CUSTOM_FONT_FAMILY,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#CBD5E1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "transparent";
          }}
        >
          {page?.body ? (
            <MarkdownPreview text={page.body} baseFontPx={baseFontPx} />
          ) : (
            <div style={{ fontSize: baseFontPx, fontStyle: "italic" }}>
              Tocá acá para escribir, o pedíle a Hornero que genere el texto e{" "}
              <strong>insertalo en la página</strong>.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Overlay del flujo de generación + share ─────────────────────────────
// Estados:
//   generating → spinner + "Generando informe…"
//   uploading  → spinner + "Guardando en tu panel…"
//   done       → check + URL + botones share (WhatsApp / Mail / Copiar / Cerrar)
//   error      → cruz + mensaje + retry / cerrar
const FlowOverlay = ({
  step,
  error,
  uploadedFile,
  reportName,
  onClose,
  onRetry,
  onGeneratePdf,
  onGenerateExcel,
}) => {
  const fmtUrl = uploadedFile?.url;
  const fileLabel = uploadedFile?.format === "excel" ? "Excel" : "PDF";
  // El otro formato que queda por generar (para el botón "Generar también").
  const otherFormat = uploadedFile?.format === "excel" ? "pdf" : "excel";
  const otherLabel = otherFormat === "pdf" ? "PDF" : "Excel";
  const otherIcon = otherFormat === "pdf" ? "pi-file-pdf" : "pi-file-excel";
  const generateOther = otherFormat === "pdf" ? onGeneratePdf : onGenerateExcel;

  const waMessage = fmtUrl
    ? `Te comparto la tasación "${reportName || "Informe"}":\n${fmtUrl}`
    : "";
  const waUrl = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;
  const mailSubject = `Tasación · ${reportName || "Informe"}`;
  const mailBody = fmtUrl
    ? `Hola,\n\nTe comparto la tasación que generé:\n${fmtUrl}\n\nSaludos.`
    : "";
  const mailUrl = `mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

  const copy = async () => {
    if (!fmtUrl) return;
    try {
      await navigator.clipboard.writeText(fmtUrl);
    } catch {}
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(255,255,255,0.96)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 14,
          padding: 28,
          boxShadow: "0 8px 32px rgba(15, 23, 42, 0.12)",
          textAlign: "center",
        }}
      >
        {step === "generating" && (
          <>
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: 42, color: "#FA7317" }}
            />
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                marginTop: 16,
              }}
            >
              Generando informe…
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 8 }}>
              Armando las páginas y la descarga local. No cierres esta ventana.
            </div>
          </>
        )}

        {step === "uploading" && (
          <>
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: 42, color: "#FA7317" }}
            />
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                marginTop: 16,
              }}
            >
              Guardando en tu panel…
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 8 }}>
              Subiendo el archivo al storage para que quede disponible en el
              dashboard.
            </div>
          </>
        )}

        {step === "done" && (
          <>
            <i
              className="pi pi-check-circle"
              style={{ fontSize: 48, color: "#FA7317" }}
            />
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                marginTop: 14,
              }}
            >
              ¡Informe listo!
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 6 }}>
              El {fileLabel} se descargó y se guardó en tu panel.
            </div>

            {fmtUrl && (
              <>
                <div
                  style={{
                    marginTop: 18,
                    padding: "10px 12px",
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    fontSize: 11,
                    color: "#475569",
                    wordBreak: "break-all",
                    textAlign: "left",
                    maxHeight: 60,
                    overflow: "auto",
                  }}
                >
                  {fmtUrl}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    marginTop: 16,
                  }}
                >
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      background: "#25D366",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    <i className="pi pi-whatsapp" />
                    WhatsApp
                  </a>
                  <a
                    href={mailUrl}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      background: "#FA7317",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    <i className="pi pi-envelope" />
                    Email
                  </a>
                  <button
                    type="button"
                    onClick={copy}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      background: "#fff",
                      color: "#0F172A",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <i className="pi pi-copy" />
                    Copiar link
                  </button>
                </div>
              </>
            )}

            {/* Generar también el otro formato (Excel si ya hizo PDF, y
                viceversa). Vuelve a disparar el flow de generación → upload
                → done, manteniendo al user dentro del editor. */}
            {generateOther && (
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px solid #E2E8F0",
                }}
              >
                <div
                  style={{ fontSize: 11, color: "#64748B", marginBottom: 8 }}
                >
                  ¿Querés también el {otherLabel}?
                </div>
                <button
                  type="button"
                  onClick={generateOther}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 16px",
                    background: "#fff",
                    color: "#0F172A",
                    border: "1px solid #CBD5E1",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <i className={`pi ${otherIcon}`} style={{ fontSize: 13 }} />
                  Generar también {otherLabel}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 14,
                padding: "8px 20px",
                background: "#FA7317",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cerrar y volver al inicio
            </button>
          </>
        )}

        {step === "error" && (
          <>
            <i
              className="pi pi-times-circle"
              style={{ fontSize: 48, color: "#DC2626" }}
            />
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0F172A",
                marginTop: 14,
              }}
            >
              Algo salió mal
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 6 }}>
              {error || "No pudimos terminar de generar el informe."}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={onRetry}
                style={{
                  padding: "8px 16px",
                  background: "#FA7317",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Reintentar
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  color: "#0F172A",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancelar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PDFPreviewEditor;
